import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { AttachedFile, Button, Label, RadioGroupForm, RecordVideoForm } from '@/components';
import {
  EmailInput,
  PhoneInput,
  CheckboxGroupForm,
  AddressBox,
  CountryInput,
  TextList,
  CurrencySelect
} from '@/modules/kycModule/Fields';
import { FormDatePicker, FormInput } from '@/modules/kycModule/components';
import { Container } from '@/ui/layout';
import { Body4Light } from '@/ui';
import { Tooltip } from 'antd';
import { Body1Light, Label2 } from '@/ui/typography';
import { FileUploadForm } from '@/modules/kycModule/Fields/FileUpload';
import { useMutation } from '@tanstack/react-query';
import { adminKYCApi } from '@/api';
import { COLORS } from '@/utils/static';
import { RejectModal } from '@/modules/Questionnaire/modals';
import { SignatureForm } from '@/modules/kycModule/Fields/SignatureForm';
import { useWatch } from 'react-hook-form';
import { ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN, STATUS } from '@/utils/static/kyc';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '@/utils/helpers';
import { usePopups } from '@/hooks';

let isRequiredSave = '';
export const Question = ({
  name,
  question,
  control,
  watch,
  trigger,
  submitCount,
  adminView,
  rejectedQuestions,
  addRejectedQuestion,
  productStatus,
  preFilledQuestions,
  disableAll,
  disableQuestion,
  groupVisibleConditions,
  productMinimalAmount,
  creators
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(
    question && question?.visibleOperator ? question?.visibleOperator === 'AND' : true
  );
  const fields = useWatch({
    control,
    name: question.notRequiredConditions ? question.notRequiredConditions.map((item) => item) : null
  });

  //isVisible logic
  if (question && question.visibleConditions && Array.from(question.visibleConditions).length > 0) {
    const conditions = question.visibleConditions;
    const operator = question.visibleOperator;
    let fields = watch(conditions.map((item) => item.id));

    let isVisible = operator === 'AND';
    for (const [index, value] of fields.entries()) {
      if (
        operator === 'AND' &&
        (Array.isArray(value)
          ? !value.includes(conditions[index].value)
          : conditions[index].value !== value)
      ) {
        isVisible = false;
        break;
      }
      if (
        operator === 'OR' &&
        (Array.isArray(value)
          ? value.includes(conditions[index].value)
          : conditions[index].value === value)
      ) {
        isVisible = true;
        break;
      }
    }

    if (visible !== isVisible) setVisible(isVisible);
  }

  //isRequiredLogic
  const isRequired = useMemo(() => {
    if (adminView || !question || !question.isRequired) return null;

    if (!question.notRequiredConditions) return t('Required');

    if (question.notRequiredConditions) {
      const triggerFields = () => {
        setTimeout(() => {
          trigger([question.id, ...question.notRequiredConditions]);
        }, 500);
      };

      let returnValue = t('One of the fields is required');

      for (const [index, value] of fields.entries()) {
        if (value) returnValue = null;
      }

      if (submitCount > 0 && isRequiredSave !== '' && isRequiredSave !== returnValue)
        triggerFields();

      return returnValue;
    }
  }, [fields, submitCount]);

  useEffect(() => {
    isRequiredSave = isRequired;
  }, [isRequired]);

  const RenderedComponent = useMemo(
    () => getRenderedComponent(question.type, adminView),
    [question.type, adminView]
  );

  const validateByType = useMemo(
    () => ({
      CONFIRMATIONS: (v) =>
        v.length === question.productAvailableAnswers.length || t('Confirm all items'),
      INPUT: (v) =>
        question.maxCountSymbols &&
        question.minCountSymbols &&
        question.countFirstLetterSymbolsCountry
          ? v.length < question.maxCountSymbols &&
            v.length > question.minCountSymbols &&
            v.match(new RegExp('^[A-Za-z]{' + question.countFirstLetterSymbolsCountry + '}'))
            ? true
            : t('Min - 15, max - 34, first two symbols must be letters')
          : true,
      MINIMAL_AMOUNT: (v) =>
        v >= productMinimalAmount ? true : `${t('Minimal amount is')} ${productMinimalAmount}`
    }),
    []
  );

  const answerCreator = creators[question?.id];

  const isRejected = useMemo(() => {
    return rejectedQuestions ? rejectedQuestions[question.id] : null;
  }, [rejectedQuestions, question]);

  const isDisabled = useMemo(() => {
    return (
      (disableAll && !isRejected && !groupVisibleConditions && !question.visibleConditions) ||
      adminView ||
      disableQuestion ||
      preFilledQuestions[question?.id]
    );
  }, [disableAll, isRejected, disableQuestion, preFilledQuestions, question]);

  if (RenderedComponent && visible) {
    const validations = validateByType[question.type];

    return (
      <Container>
        {/*{question.question && <Body2Light>{question.question}</Body2Light>}*/}
        <RenderedComponent
          form={{
            name: name || question.id,
            control,
            rules: {
              required: isRequired,
              validate: validations
            }
          }}
          label={question.question}
          tooltip={question ? question.tooltip : ''}
          tooltip_RU={question ? question.tooltip_RU : ''}
          tooltip_LV={question ? question.tooltip_LV : ''}
          question={question}
          adminView={adminView}
          disabled={isDisabled}
          {...(question.type === 'INPUT' ? { clearRegex: question.clearRegex } : {})}
          required={isRequired}
        />

        {answerCreator && (
          <Body4Light $marginTop={10} $color={COLORS.aragonDarkBlue}>
            {t('Filled by')} {answerCreator.firstName} {answerCreator.lastName}
          </Body4Light>
        )}

        {!adminView && isRejected && (
          <Body1Light $marginTop={12} $color={COLORS.red}>
            {isRejected}
          </Body1Light>
        )}

        {adminView && addRejectedQuestion && (
          <AdminRejectButton
            questionId={question.id}
            addRejectedQuestion={addRejectedQuestion}
            rejected={isRejected}
            disabled={!ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[productStatus]}
          />
        )}
      </Container>
    );
  }

  return null;
};

const AdminRejectButton = ({ questionId, rejected, addRejectedQuestion, disabled }) => {
  const { t } = useTranslation();
  const { id, productId } = useParams();
  const { showErrorToast } = usePopups();
  const [isModalActive, setModalActive] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const { mutate: rejectQuestion } = useMutation({
    mutationFn: (comment) =>
      adminKYCApi.setProductStatus({
        investorId: id,
        status: STATUS.REJECTED,
        questionId: questionId,
        questionnaireId: productId,
        comment: comment
      })
  });

  const onReject = async () => {
    try {
      if (comment) {
        await rejectQuestion(comment);
        addRejectedQuestion(questionId, comment);
        setModalActive(false);
      } else {
        setError(t('Required'));
      }
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };

  return (
    <Container>
      <Button
        size='small'
        text={rejected ? t('Rejected') : t('Reject question')}
        onClick={() => setModalActive(true)}
        type='text'
        block={false}
        disabled={disabled || rejected}
        style={{ color: COLORS.red }}
      />

      <RejectModal
        active={isModalActive}
        onClose={() => setModalActive(false)}
        comment={comment}
        setComment={setComment}
        error={error}
        onSubmit={onReject}
      />
    </Container>
  );
};

const FormatRadioGroupForm = ({ question, adminView, required, ...props }) => {
  const { productAvailableAnswers, answers, ...questionRest } = question;
  const { i18n } = useTranslation();

  return (
    <div>
      {question.question && (
        <Label
          required={required}
          label={question.question}
          marginBottom={12}
          tooltip={question.tooltip ? getLocalizedText(question, 'tooltip', i18n) : null}
        >
          <RadioGroupForm
            items={
              productAvailableAnswers ? productAvailableAnswers.map((item) => item.answer) : []
            }
            ids={productAvailableAnswers ? productAvailableAnswers.map((item) => item.id) : []}
            {...questionRest}
            {...props}
          />
        </Label>
      )}
    </div>
  );
};

const RenderFileView = ({ label, tooltip, form }) => {
  const nameArray = form.name.split('.');

  const getValue = (values, nameArray) => {
    if (values) {
      return nameArray.length === 1
        ? values[nameArray[0]]
        : nameArray.reduce((result, current) => result[current], values);
    }

    return null;
  };

  try {
    return (
      <div>
        {label && tooltip ? (
          <Tooltip title={tooltip}>
            <Label2>{label}</Label2>
          </Tooltip>
        ) : label ? (
          <Label2>{label}</Label2>
        ) : null}
        <AttachedFile url={getValue(form?.control['_formValues'], nameArray)} />
      </div>
    );
  } catch (e) {}
};

const getRenderedComponent = (type, isAdmin) => {
  const component = isAdmin ? adminComponentByType[type] : componentByType[type];
  return component || FormInput;
};
const componentByType = {
  EMAIL: EmailInput,
  PHONE: PhoneInput,
  INPUT: FormInput,
  NUMBER: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  INTEGER: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  MINIMAL_AMOUNT: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  CURRENCY: CurrencySelect,
  COUNTRY: CountryInput,
  ADDRESS: AddressBox,
  RADIO: FormatRadioGroupForm,
  CHECKBOX: CheckboxGroupForm,
  CONFIRMATIONS: CheckboxGroupForm,
  DOC_UPLOAD: FileUploadForm,
  RECORD_VIDEO: RecordVideoForm,
  DATE: FormDatePicker,
  SIGNATURE: SignatureForm,
  TEXT: TextList,
  IBAN: TextList,
  SWIFT: TextList
};
const adminComponentByType = {
  EMAIL: FormInput,
  PHONE: FormInput,
  INPUT: FormInput,
  INTEGER: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  MINIMAL_AMOUNT: (props) => <FormInput {...props} clearRegex={/[^0-9]/g} />,
  CURRENCY: CurrencySelect,
  NUMBER: FormInput,
  COUNTRY: FormInput,
  ADDRESS: AddressBox,
  RADIO: FormatRadioGroupForm,
  CHECKBOX: CheckboxGroupForm,
  CONFIRMATIONS: CheckboxGroupForm,
  DATE: FormDatePicker,
  TEXT: TextList,
  IBAN: TextList,
  SWIFT: TextList,
  SIGNATURE: RenderFileView,
  DOC_UPLOAD: RenderFileView,
  RECORD_VIDEO: RenderFileView
};
